import React from 'react';
import { Users, Bed, Droplet, Wifi, Check, Info } from 'lucide-react';
import { rooms, tariffOptions, homestayInfo } from '../data/mockData';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Alert, AlertDescription } from '../components/ui/alert';

const Rooms = () => {
  const handleWhatsAppClick = () => {
    const message = encodeURIComponent("Hello! I'd like to inquire about room availability and booking.");
    window.open(`https://wa.me/${homestayInfo.whatsapp.replace(/[^0-9]/g, '')}?text=${message}`, '_blank');
  };

  return (
    <div className="min-h-screen pt-20">
      {/* Header */}
      <section className="py-16 bg-gradient-to-br from-[#1F4D3A] to-[#2d6b52] text-white">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-6xl font-bold mb-4">Our Rooms</h1>
          <p className="text-xl text-white/90 max-w-2xl mx-auto">
            Choose from 4 comfortable rooms designed for a peaceful stay
          </p>
        </div>
      </section>

      {/* Important Note */}
      <section className="py-8 bg-[#F7F5F0]">
        <div className="container mx-auto px-4">
          <Alert className="max-w-4xl mx-auto bg-[#A3B18A]/10 border-[#A3B18A]">
            <Info className="h-5 w-5 text-[#1F4D3A]" />
            <AlertDescription className="text-gray-700 ml-2">
              <strong>Homestay Layout:</strong> As a homestay, each room has its unique charm and configuration. Detached bathrooms are very close to respective rooms and exclusively dedicated for your use. All bathrooms feature western commode and hot water geyser.
            </AlertDescription>
          </Alert>
        </div>
      </section>

      {/* Rooms Grid */}
      <section className="py-16 bg-[#F7F5F0]">
        <div className="container mx-auto px-4">
          <div className="space-y-8 max-w-6xl mx-auto">
            {rooms.map((room, index) => (
              <Card key={room.id} className={`overflow-hidden hover:shadow-2xl transition-all duration-300 ${
                room.featured ? 'ring-2 ring-[#D4A373]' : ''
              }`}>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
                  {/* Images */}
                  <div className={`relative h-80 lg:h-auto ${index % 2 === 1 ? 'lg:order-2' : ''}`}>
                    <div className="grid grid-cols-2 h-full">
                      {room.images.map((img, imgIndex) => (
                        <div key={imgIndex} className="relative overflow-hidden group">
                          <img
                            src={img}
                            alt={`${room.name} - View ${imgIndex + 1}`}
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                          />
                        </div>
                      ))}
                    </div>
                    {room.featured && (
                      <div className="absolute top-4 right-4 bg-[#D4A373] text-white px-4 py-2 rounded-full text-sm font-semibold shadow-lg">
                        Best View
                      </div>
                    )}
                  </div>

                  {/* Details */}
                  <div className={`p-8 ${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                    <div className="space-y-6">
                      <div>
                        <h2 className="text-3xl font-bold text-[#1F4D3A] mb-2">{room.name}</h2>
                        <p className="text-lg text-[#A3B18A] font-medium">{room.view}</p>
                      </div>

                      {/* Room Specifications */}
                      <div className="grid grid-cols-2 gap-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-[#1F4D3A]/10 rounded-lg flex items-center justify-center">
                            <Users className="w-5 h-5 text-[#1F4D3A]" />
                          </div>
                          <div>
                            <p className="text-sm text-gray-600">Capacity</p>
                            <p className="font-semibold text-[#1F4D3A]">{room.capacity} Persons</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-[#1F4D3A]/10 rounded-lg flex items-center justify-center">
                            <Bed className="w-5 h-5 text-[#1F4D3A]" />
                          </div>
                          <div>
                            <p className="text-sm text-gray-600">Beds</p>
                            <p className="font-semibold text-[#1F4D3A]">{room.beds}</p>
                          </div>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <p className="text-sm text-gray-600"><strong>Room Size:</strong> {room.size}</p>
                        <p className="text-sm text-gray-600"><strong>Bathroom:</strong> {room.bathroom}</p>
                      </div>

                      {/* Amenities */}
                      <div>
                        <h3 className="font-semibold text-[#1F4D3A] mb-3">Amenities</h3>
                        <div className="space-y-2">
                          {room.amenities.map((amenity, i) => (
                            <div key={i} className="flex items-center gap-2">
                              <Check className="w-4 h-4 text-[#25D366]" />
                              <span className="text-sm text-gray-700">{amenity}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Pricing */}
                      <div className="pt-4 border-t border-gray-200">
                        <h3 className="font-semibold text-[#1F4D3A] mb-3">Tariff</h3>
                        <div className="space-y-2">
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600">Food + Lodging</span>
                            <span className="text-lg font-bold text-[#1F4D3A]">₹{room.tariff.withFood}/person</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600">Lodging Only</span>
                            <span className="text-lg font-bold text-[#1F4D3A]">₹{room.tariff.lodgingOnly}/room</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Tariff Details */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-[#1F4D3A] mb-4">Tariff Details</h2>
              <p className="text-lg text-gray-600">Choose the package that suits you best</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {tariffOptions.map((option) => (
                <Card key={option.id} className={`${
                  option.recommended ? 'ring-2 ring-[#1F4D3A] shadow-xl' : ''
                }`}>
                  <CardContent className="p-8">
                    {option.recommended && (
                      <div className="mb-4">
                        <span className="bg-[#1F4D3A] text-white px-3 py-1 rounded-full text-sm font-semibold">
                          Recommended
                        </span>
                      </div>
                    )}
                    <h3 className="text-2xl font-bold text-[#1F4D3A] mb-2">{option.name}</h3>
                    {option.price && (
                      <p className="text-3xl font-bold text-[#D4A373] mb-6">{option.price}</p>
                    )}
                    {option.includes && (
                      <div className="space-y-3">
                        <p className="font-semibold text-gray-700">Includes:</p>
                        {option.includes.map((item, i) => (
                          <div key={i} className="flex items-start gap-3">
                            <Check className="w-5 h-5 text-[#25D366] flex-shrink-0 mt-0.5" />
                            <span className="text-gray-700">{item}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {option.rooms && (
                      <div className="space-y-4">
                        {option.rooms.map((room, i) => (
                          <div key={i} className="flex justify-between items-center py-3 border-b border-gray-200">
                            <span className="font-medium text-gray-700">{room.name}</span>
                            <span className="text-xl font-bold text-[#1F4D3A]">{room.price}</span>
                          </div>
                        ))}
                        {option.note && (
                          <p className="text-sm text-gray-600 italic mt-4">{option.note}</p>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-br from-[#1F4D3A] to-[#2d6b52] text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Book Your Stay Today</h2>
          <p className="text-lg text-white/90 mb-8 max-w-2xl mx-auto">
            Experience the comfort and warmth of White Orchid Homestay
          </p>
          <Button
            size="lg"
            onClick={handleWhatsAppClick}
            className="bg-[#25D366] hover:bg-[#20BA5A] text-white text-lg px-8 py-6"
          >
            Check Availability on WhatsApp
          </Button>
        </div>
      </section>
    </div>
  );
};

export default Rooms;
